import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { apiLogin, apiSession, apiLogout, setCsrfToken } from '../lib/api';

const AuthCtx = createContext(null);

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [mustChangePassword, setMustChangePassword] = useState(false);
    const [isLoading, setIsLoading] = useState(true);

    // Rehydrate on mount: ask the server if our cookie is valid. If so,
    // the response includes the current csrf token so state-changing
    // calls work immediately on a full page reload.
    useEffect(() => {
        let cancelled = false;
        apiSession()
            .then((data) => {
                if (cancelled) return;
                setUser(data.user);
                setCsrfToken(data.csrf_token);
                setMustChangePassword(!!data.must_change_password);
            })
            .catch(() => {
                if (cancelled) return;
                setCsrfToken(null);
                setUser(null);
                setMustChangePassword(false);
            })
            .finally(() => {
                if (!cancelled) setIsLoading(false);
            });
        return () => { cancelled = true; };
    }, []);

    const login = useCallback(async (email, password) => {
        const data = await apiLogin(email, password);
        setCsrfToken(data.csrf_token);
        setUser(data.user);
        setMustChangePassword(!!data.must_change_password);
        return { user: data.user, mustChangePassword: !!data.must_change_password };
    }, []);

    const logout = useCallback(async () => {
        try {
            await apiLogout();
        } catch (err) {
            if (process.env.NODE_ENV !== 'production') {
                // eslint-disable-next-line no-console
                console.warn('apiLogout failed; clearing local session anyway:', err);
            }
        }
        setCsrfToken(null);
        setUser(null);
        setMustChangePassword(false);
    }, []);

    // Called after a successful change-password response - the new csrf
    // token comes back in the response body (because the backend re-
    // issued the cookie with must_change_password=false).
    const clearForcedRotation = useCallback((newCsrf) => {
        if (newCsrf) setCsrfToken(newCsrf);
        setMustChangePassword(false);
    }, []);

    return (
        <AuthCtx.Provider
            value={{
                user,
                isLoading,
                login,
                logout,
                mustChangePassword,
                clearForcedRotation,
            }}
        >
            {children}
        </AuthCtx.Provider>
    );
};

export const useAuth = () => {
    const ctx = useContext(AuthCtx);
    if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
    return ctx;
};
